// src/quality/QualityWidgets.tsx
// 5 виджетов раздела «Контроль качества медицинских карт». Самодостаточные:
// каждый сам грузит данные (кэш в qc.ts дедупит summary/breakdown). Стиль/цвета —
// как в дашборде (ECharts, палитра QC_COLORS).
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import ReactECharts from "echarts-for-react";
import {
  QC_COLORS,
  fetchQcSummary,
  fetchQcBreakdown,
  fetchQcDynamics,
  monthShort,
  weekShort,
  openQualityCardsTab,
} from "./qc";
import type { QcScope, QcFilters, QcSummary, QcBreakdown, QcDynamics } from "./qc";

const TOOLTIP = {
  backgroundColor: QC_COLORS.page,
  borderColor: "rgba(255,255,255,0.3)",
  borderWidth: 1,
  textStyle: { color: "#fff" },
  extraCssText: "border-radius:10px;",
};

const EMPTY_TEXT = "За выбранный период данные отсутствуют";

interface WidgetProps {
  scope: QcScope;
  filters: QcFilters;
}

// Полноэкранное состояние держим в самом виджете, чтобы график знал про режим
// (например, шире колонку названий на весь экран).
function useFullscreen() {
  const [full, setFull] = useState(false);
  const toggle = () => {
    setFull((f) => !f);
    // ECharts подхватывает новый размер контейнера только по событию resize
    setTimeout(() => window.dispatchEvent(new Event("resize")), 60);
  };
  return { full, toggle };
}

// ---- общий контейнер-карточка (с кнопкой «Развернуть/Свернуть» как у остальных плиток) ----
function QcCard(props: { title: string; children: ReactNode; subtitle?: string; full: boolean; onToggle: () => void }) {
  const full = props.full;
  const toggle = props.onToggle;
  const overlayZ = 15000;
  const cardZ = 16000;
  return (
    <div style={{ height: "100%", minHeight: 0 }}>
      {full ? (
        <div style={{ position: "fixed", inset: 0, background: "rgba(10,5,20,0.55)", zIndex: overlayZ }} onClick={toggle} />
      ) : null}
      <section
        style={{
          boxSizing: "border-box",
          background: QC_COLORS.card,
          color: "#fff",
          border: `1px solid ${QC_COLORS.border}`,
          borderRadius: 12,
          padding: 12,
          boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
          minHeight: 0,
          height: full ? "calc(100vh - 32px)" : "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: full ? "fixed" : "relative",
          left: full ? "50%" : undefined,
          top: full ? "50%" : undefined,
          transform: full ? "translate(-50%, -50%)" : undefined,
          width: full ? "min(1400px, calc(100vw - 32px))" : "100%",
          zIndex: full ? cardZ : "auto",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, minWidth: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: full ? 20 : 15, fontWeight: 700, lineHeight: 1.25 }}>{props.title}</div>
            {props.subtitle ? (
              <div style={{ fontSize: 12, color: QC_COLORS.muted, marginTop: 2 }}>{props.subtitle}</div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={toggle}
            title={full ? "Свернуть" : "Развернуть"}
            style={{
              height: 32,
              padding: "0 12px",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.28)",
              background: "rgba(255,255,255,0.08)",
              color: "#fff",
              cursor: "pointer",
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {full ? "Свернуть" : "Развернуть"}
          </button>
        </div>
        <div style={{ flex: 1, minHeight: 0, marginTop: 10, overflow: "hidden" }}>{props.children}</div>
      </section>
    </div>
  );
}

function CenterMsg({ text }: { text: string }) {
  return (
    <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: QC_COLORS.muted, textAlign: "center", padding: 12 }}>
      {text}
    </div>
  );
}

// generic loader hook
function useQc<T>(loader: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true); // грузим сразу на маунте — не рендерить контент до данных
  const [error, setError] = useState<string>("");
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    loader()
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e?.message || "Ошибка загрузки"))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { data, loading, error };
}

function stateNode(loading: boolean, error: string, empty: boolean) {
  if (loading) return <CenterMsg text="Загрузка…" />;
  if (error) return <CenterMsg text={error} />;
  if (empty) return <CenterMsg text={EMPTY_TEXT} />;
  return null;
}

// SVG-кольцо процента для KPI
function Ring({ pct, color, size = 42 }: { pct: number; color: string; size?: number }) {
  const sw = Math.max(4, Math.round(size * 0.12));
  const ctr = size / 2;
  const r = ctr - sw;
  const c = 2 * Math.PI * r;
  const off = c * (1 - Math.max(0, Math.min(100, pct)) / 100);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle cx={ctr} cy={ctr} r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth={sw} />
      <circle
        cx={ctr}
        cy={ctr}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={sw}
        strokeDasharray={c}
        strokeDashoffset={off}
        strokeLinecap="round"
        transform={`rotate(-90 ${ctr} ${ctr})`}
      />
      <text x={ctr} y={ctr + size * 0.09} textAnchor="middle" fontSize={Math.round(size * 0.26)} fill="#fff">{`${Math.round(pct)}%`}</text>
    </svg>
  );
}

// ============ Блок 1: KPI-карточки ============
export function QualityKpi({ scope, filters }: WidgetProps) {
  const { full, toggle } = useFullscreen();
  const { data, loading, error } = useQc<QcSummary>(
    () => fetchQcSummary(scope, filters),
    [scope, filters.mis_id, filters.place, filters.year, filters.month, filters.day, filters.date_from, filters.date_to, filters.bed_profiles]
  );
  const s = stateNode(loading, error, !data?.kpi || data.kpi.checked_cards === 0);
  const k = data?.kpi;

  // Компактный формат денег для узкой плитки.
  const money = (v: number) => {
    if (v >= 1e6) return (v / 1e6).toLocaleString("ru-RU", { maximumFractionDigits: 1 }) + " млн ₸";
    if (v >= 1e3) return Math.round(v / 1e3).toLocaleString("ru-RU") + " тыс ₸";
    return Math.round(v).toLocaleString("ru-RU") + " ₸";
  };

  // Плитки: вертикальный стек — кольцо % СВЕРХУ, число ПОСЕРЕДИНЕ (крупно, на всю ширину),
  // подпись снизу. Шрифт числа масштабируется от ширины плитки (container-queries), чтобы
  // цифры читались при раскладке 4/6 на экране.
  const tile = (accent: string, big: React.ReactNode, label: string, extra?: React.ReactNode, onClick?: () => void) => (
    <div style={{ flex: "1 1 0", minWidth: 0, containerType: "inline-size", background: "rgba(255,255,255,0.04)", border: `1px solid ${QC_COLORS.border}`, borderRadius: 12, padding: "6px 8px", position: "relative", overflow: "hidden", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, cursor: onClick ? "pointer" : "default" } as React.CSSProperties} onClick={onClick}>
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: accent }} />
      {extra ? <div style={{ lineHeight: 0, flexShrink: 0 }}>{extra}</div> : null}
      <div style={{ fontSize: "clamp(13px, 24cqw, 26px)", fontWeight: 700, lineHeight: 1.05, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%", textAlign: "center" }}>{big}</div>
      <div style={{ fontSize: "clamp(9px, 7cqw, 12px)", color: QC_COLORS.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%", textAlign: "center" }}>{label}</div>
    </div>
  );

  return (
    <QcCard title="Контроль качества медицинских карт" full={full} onToggle={toggle}>
      {s ?? (
        <div style={{ display: "flex", gap: 10, height: "100%", alignItems: "stretch", flexWrap: "nowrap" }}>
          {tile("#48B1CC", k!.checked_cards.toLocaleString("ru-RU"), "Проверено карт", undefined, () => openQualityCardsTab(scope, filters, "all", {}, "Проверенные карты"))}
          {tile("#DC143C", k!.defective_cards.toLocaleString("ru-RU"), "Дефектных карт", undefined, () => openQualityCardsTab(scope, filters, "defective", {}, "Дефектные карты"))}
          {tile("#48B1CC", k!.fixed_cards.toLocaleString("ru-RU"), "Исправлено", <Ring pct={k!.fixed_pct} color={QC_COLORS.fixed} size={36} />, () => openQualityCardsTab(scope, filters, "fixed", {}, "Исправленные карты"))}
          {tile("#DC143C", k!.open_cards.toLocaleString("ru-RU"), "Не исправлено", <Ring pct={k!.open_pct} color={QC_COLORS.open} size={36} />, () => openQualityCardsTab(scope, filters, "open", {}, "Не исправленные карты"))}
          {scope === "app"
            ? tile("#48B1CC", money(k!.saved_cost ?? 0), "Сэкономлено")
            : tile("#eee36d", k!.critical_cards.toLocaleString("ru-RU"), "Критических (>38 ч)", undefined, () => openQualityCardsTab(scope, filters, "critical", {}, "Критические карты"))}
        </div>
      )}
    </QcCard>
  );
}

// ============ Блок 2: Donut «Статус устранения» ============
export function QualityDonut({ scope, filters }: WidgetProps) {
  const { full, toggle } = useFullscreen();
  const { data, loading, error } = useQc<QcSummary>(
    () => fetchQcSummary(scope, filters),
    [scope, filters.mis_id, filters.place, filters.year, filters.month, filters.day, filters.date_from, filters.date_to, filters.bed_profiles]
  );
  const total = data?.donut ? data.donut.reduce((a, b) => a + b.value, 0) : 0;
  const s = stateNode(loading, error, !data?.donut || total === 0);
  const option = useMemo(() => {
    if (!data?.donut) return {};
    return {
      tooltip: { ...TOOLTIP, trigger: "item", formatter: (p: any) => `${p.name}: ${p.value} (${p.percent}%)` },
      legend: { bottom: 0, textStyle: { color: "#fff" } },
      color: [QC_COLORS.fixed, QC_COLORS.open],
      series: [
        {
          type: "pie",
          radius: ["55%", "78%"],
          center: ["50%", "44%"],
          avoidLabelOverlap: true,
          label: { show: true, position: "center", formatter: `${total}\nдефектных`, color: "#fff", fontSize: 16, lineHeight: 18 },
          labelLine: { show: false },
          data: data.donut.map((d) => ({ name: d.label, value: d.value })),
        },
      ],
    };
  }, [data, total]);

  return (
    <QcCard title="Статус устранения дефектов" subtitle="Доля исправленных и открытых замечаний ИИ Дефектолога" full={full} onToggle={toggle}>
      {s ?? <ReactECharts option={option} style={{ height: "100%", width: "100%" }} notMerge lazyUpdate onEvents={{ click: (p: any) => openQualityCardsTab(scope, filters, p?.name === "Исправлено" ? "fixed" : "open", {}, String(p?.name || "Карты")) }} />}
    </QcCard>
  );
}

// ============ Блок 3: ТОП-5 типов дефектов (горизонтальный bar) ============
export function QualityTypes({ scope, filters }: WidgetProps) {
  const { full, toggle } = useFullscreen();
  const { data, loading, error } = useQc<QcBreakdown>(
    () => fetchQcBreakdown(scope, filters),
    [scope, filters.mis_id, filters.place, filters.year, filters.month, filters.day, filters.date_from, filters.date_to, filters.bed_profiles]
  );
  const s = stateNode(loading, error, !data?.types || data.types.length === 0);
  const option = useMemo(() => {
    if (!data?.types) return {};
    const rows = [...data.types].reverse(); // ECharts рисует снизу вверх
    // На весь экран — шире колонка названий и перенос по словам, чтобы имя влезало целиком.
    const labelWidth = full ? 460 : 170;
    return {
      tooltip: { ...TOOLTIP, trigger: "axis", axisPointer: { type: "shadow" } },
      grid: { left: 8, right: 48, top: 10, bottom: 10, containLabel: true },
      xAxis: { type: "value", axisLabel: { color: QC_COLORS.muted }, splitLine: { lineStyle: { color: "rgba(255,255,255,0.1)" } } },
      yAxis: {
        type: "category",
        data: rows.map((r) => r.label),
        axisLabel: { color: "#fff", interval: 0, width: labelWidth, overflow: full ? "break" : "truncate", lineHeight: 15 },
      },
      series: [
        {
          type: "bar",
          data: rows.map((r) => r.cards),
          itemStyle: { color: QC_COLORS.barAccent, borderRadius: [0, 6, 6, 0] },
          label: { show: true, position: "right", color: "#fff" },
        },
      ],
    };
  }, [data, full]);

  return (
    <QcCard title="ТОП-5 типов дефектов" subtitle="В скольких картах встречается тип" full={full} onToggle={toggle}>
      {s ?? <ReactECharts option={option} style={{ height: "100%", width: "100%" }} notMerge lazyUpdate onEvents={{ click: (p: any) => { if (p?.name) openQualityCardsTab(scope, filters, "type", { type_label: p.name }, "Тип: " + p.name); } }} />}
    </QcCard>
  );
}

// ============ Блок 4: Рейтинг отделений (% чистых карт) ============
export function QualityDepartments({ scope, filters }: WidgetProps) {
  const { full, toggle } = useFullscreen();
  const [worstFirst, setWorstFirst] = useState(true);
  const { data, loading, error } = useQc<QcBreakdown>(
    () => fetchQcBreakdown(scope, filters),
    [scope, filters.mis_id, filters.place, filters.year, filters.month, filters.day, filters.date_from, filters.date_to, filters.bed_profiles]
  );
  const s = stateNode(loading, error, !data?.departments || data.departments.length === 0);
  const option = useMemo(() => {
    if (!data?.departments) return {};
    // % качества = карты без дефектов / все проверенные * 100
    const sorted = [...data.departments].sort((a, b) => (worstFirst ? a.clean_pct - b.clean_pct : b.clean_pct - a.clean_pct));
    const top = sorted.slice(0, full ? 20 : 12).reverse();
    // На весь экран — шире колонка отделений и перенос по словам.
    const labelWidth = full ? 420 : 150;
    return {
      tooltip: {
        ...TOOLTIP,
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (ps: any) => {
          const d = top[ps[0].dataIndex];
          return `${d.label}<br/>Качество: ${d.clean_pct}%<br/>Проверено: ${d.checked} / дефектов: ${d.defective}`;
        },
      },
      grid: { left: 8, right: 46, top: 10, bottom: 10, containLabel: true },
      xAxis: { type: "value", max: 100, axisLabel: { color: QC_COLORS.muted, formatter: "{value}%" }, splitLine: { lineStyle: { color: "rgba(255,255,255,0.1)" } } },
      yAxis: { type: "category", data: top.map((d) => d.label), axisLabel: { color: "#fff", interval: 0, width: labelWidth, overflow: full ? "break" : "truncate", lineHeight: 15 } },
      series: [
        {
          type: "bar",
          data: top.map((d) => ({
            value: d.clean_pct,
            itemStyle: { color: d.clean_pct >= 70 ? QC_COLORS.fixed : d.clean_pct >= 40 ? QC_COLORS.critical : QC_COLORS.open, borderRadius: [0, 6, 6, 0] },
          })),
          label: { show: true, position: "right", color: "#fff", formatter: "{c}%" },
        },
      ],
    };
  }, [data, worstFirst, full]);

  return (
    <QcCard title="Качество ведения документации, %" subtitle="Карты без дефектов ÷ все проверенные" full={full} onToggle={toggle}>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
        <button
          onClick={() => setWorstFirst((v) => !v)}
          style={{ background: "transparent", color: QC_COLORS.muted, border: `1px solid ${QC_COLORS.border}`, borderRadius: 8, padding: "2px 10px", fontSize: 12, cursor: "pointer" }}
        >
          {worstFirst ? "Худшие сверху" : "Лучшие сверху"}
        </button>
      </div>
      <div style={{ height: "calc(100% - 26px)" }}>
        {s ?? <ReactECharts option={option} style={{ height: "100%", width: "100%" }} notMerge lazyUpdate onEvents={{ click: (p: any) => { if (p?.name) openQualityCardsTab(scope, filters, "department", { department_label: p.name }, "Отделение: " + p.name); } }} />}
      </div>
    </QcCard>
  );
}

// ============ Блок 5: Динамика дефектных карт (линия) ============
export function QualityDynamics({ scope, filters }: WidgetProps) {
  const { full, toggle } = useFullscreen();
  const { data, loading, error } = useQc<QcDynamics>(
    () => fetchQcDynamics(scope, filters),
    [scope, filters.mis_id, filters.place, filters.year, filters.month, filters.day, filters.date_from, filters.date_to, filters.bed_profiles]
  );
  const s = stateNode(loading, error, !data?.points || data.points.length === 0);
  const option = useMemo(() => {
    if (!data?.points) return {};
    const fmt = data.bucket_kind === "month" ? monthShort : weekShort;
    return {
      tooltip: { ...TOOLTIP, trigger: "axis" },
      grid: { left: 8, right: 20, top: 16, bottom: 24, containLabel: true },
      xAxis: { type: "category", data: data.points.map((p) => fmt(p.bucket)), axisLabel: { color: QC_COLORS.muted } },
      yAxis: { type: "value", axisLabel: { color: QC_COLORS.muted }, splitLine: { lineStyle: { color: "rgba(255,255,255,0.1)" } } },
      series: [
        {
          type: "line",
          smooth: true,
          data: data.points.map((p) => p.defective_cards),
          symbolSize: 7,
          lineStyle: { color: QC_COLORS.barAccent, width: 2 },
          itemStyle: { color: QC_COLORS.barAccent },
          areaStyle: { color: "rgba(72,177,204,0.20)" },
        },
      ],
    };
  }, [data]);

  return (
    <QcCard title="Динамика дефектов" subtitle={data?.bucket_kind === "day" ? "Дефектных карт по дням (минимум неделя)" : data?.bucket_kind === "week" ? "Дефектных карт по неделям" : "Дефектных карт по месяцам"} full={full} onToggle={toggle}>
      {s ?? <ReactECharts option={option} style={{ height: "100%", width: "100%" }} notMerge lazyUpdate onEvents={{ click: (p: any) => { const pt = data?.points?.[p?.dataIndex]; if (pt) openQualityCardsTab(scope, filters, "bucket", { bucket: pt.bucket }, "Период"); } }} />}
    </QcCard>
  );
}

// Хелпер: собрать 5 slide-элементов раздела для вставки в массив слайдов дашборда.
export function buildQualitySlides(scope: QcScope, filters: QcFilters): { id: string; element: JSX.Element }[] {
  const p = { scope, filters };
  return [
    { id: `quality-${scope}-kpi`, element: <QualityKpi {...p} /> },
    { id: `quality-${scope}-donut`, element: <QualityDonut {...p} /> },
    { id: `quality-${scope}-types`, element: <QualityTypes {...p} /> },
    { id: `quality-${scope}-departments`, element: <QualityDepartments {...p} /> },
    { id: `quality-${scope}-dynamics`, element: <QualityDynamics {...p} /> },
  ];
}
